import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useCreatePricingPlanTickRate } from 'src/features/pricingPlan/api/tickRate/useCreatePricingPlanTickRate';
import { LIST_PRICING_PLAN_TICK_RATES_RESOURCE } from 'src/features/pricingPlan/api/tickRate/useListPricingPlanTickRates';
import TickRateFormModal from 'src/features/pricingPlan/components/form/tickRate/TickRateFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface TickRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTickRate?: PricingPlanTickRate;
    children?: React.ReactNode;
}

function CreateTickRateFormModal<TData>({ className, semantics }: TickRateFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const toastShown = React.useRef(false);

    const { mutate, isPending } = useCreatePricingPlanTickRate({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_TICK_RATES_RESOURCE] });

            if (!toastShown.current) {
                toastShown.current = true;
                toast({
                    title: formatMessage(pricingPlanMessages.createTickRateSuccess),
                    description: formatMessage(pricingPlanMessages.createTickRateSuccessDescription),
                });
            }
        },
    });

    return (
        <TickRateFormModal
            className={className}
            showToast={toastShown}
            mutate={(values, onSuccess, onError) => mutate({ planId, parkingId, ...values }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.addRateSubtitle)}
        />
    );
}

export default CreateTickRateFormModal;
